import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  executeCollapseAction,
  discardCluster,
  fetchClusterDetail,
  fetchCollapseMonitor,
  fetchClusterSettings,
  fetchClusterSummary,
  fetchCryptoBubbles,
  fetchInstrumentSnapshot,
  fetchNewsFeed,
  fetchSentimentData,
  fetchWatchlist,
  sendClusterNotification,
  triggerClusterAnalysis,
  updateClusterSettings,
} from "@app/services/marketDataApi";
import type { ClusterSettingsPayload } from "@app/services/marketDataApi";

const MARKET_DATA_KEYS = {
  watchlist: ["market-data", "watchlist"] as const,
  snapshot: (symbol: string | null) => ["market-data", "snapshot", symbol] as const,
  sentiment: ["market-data", "sentiment"] as const,
  clusterSettings: ["market-data", "cluster-settings"] as const,
  clusterSummary: ["market-data", "cluster-summary"] as const,
  clusterDetail: (symbol: string | null) => ["market-data", "cluster-detail", symbol] as const,
  newsFeed: ["market-data", "news-feed"] as const,
  collapseMonitor: ["market-data", "collapse-monitor"] as const,
  cryptoBubbles: ["market-data", "crypto-bubbles"] as const,
};

/**
 * React Query hook for fetching watchlist data.
 * @returns Query result with watchlist entries
 */
export function useWatchlistData() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.watchlist,
    queryFn: fetchWatchlist,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query hook for fetching instrument snapshot data.
 * Only executes when a valid symbol is provided.
 * @param symbol - Trading symbol or null
 * @returns Query result with snapshot data
 */
export function useInstrumentSnapshot(symbol: string | null) {
  const isValidSymbol = Boolean(symbol && symbol.trim() !== "");
  
  return useQuery({
    queryKey: MARKET_DATA_KEYS.snapshot(symbol ?? ""),
    queryFn: () => {
      if (!isValidSymbol) {
        throw new Error("Symbol is required");
      }
      return fetchInstrumentSnapshot(symbol!);
    },
    enabled: isValidSymbol,
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) or when symbol is invalid
      if (error instanceof Error) {
        if (error.message.includes("required") || error.message.includes("404")) {
          return false;
        }
        // Don't retry on connection errors more than once
        if (error.message.includes("Failed to fetch") && failureCount >= 1) {
          return false;
        }
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query hook for fetching sentiment data.
 * @returns Query result with sentiment indicators and liquidity flow
 */
export function useSentimentData() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.sentiment,
    queryFn: fetchSentimentData,
    staleTime: 10 * 60_000, // 10 minutes - Fear & Greed Index updates once daily
    refetchInterval: 10 * 60_000, // Refresh every 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query hook for fetching cluster settings.
 * @returns Query result with cluster settings configuration
 */
export function useClusterSettings() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.clusterSettings,
    queryFn: fetchClusterSettings,
    staleTime: 0,
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query mutation hook for updating cluster settings.
 * @returns Mutation object for updating cluster settings
 */
export function useUpdateClusterSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClusterSettingsPayload) => updateClusterSettings(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(MARKET_DATA_KEYS.clusterSettings, data);
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.clusterSummary });
    },
  });
}

/**
 * React Query hook for fetching cluster summary data.
 * @returns Query result with cluster summary entries
 */
export function useClusterSummary() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.clusterSummary,
    queryFn: fetchClusterSummary,
    refetchInterval: 60_000, // Reduced from 45s to 60s to reduce server load
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query hook for fetching cluster detail data.
 * Only executes when a valid symbol is provided.
 * @param symbol - Trading symbol or null
 * @returns Query result with cluster detail data
 */
export function useClusterDetail(symbol: string | null) {
  const isValidSymbol = Boolean(symbol && symbol.trim() !== "");
  
  return useQuery({
    queryKey: MARKET_DATA_KEYS.clusterDetail(symbol ?? ""),
    queryFn: () => {
      if (!isValidSymbol) {
        throw new Error("Symbol is required");
      }
      return fetchClusterDetail(symbol!);
    },
    enabled: isValidSymbol,
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx) or when symbol is invalid
      if (error instanceof Error) {
        if (error.message.includes("required") || error.message.includes("404")) {
          return false;
        }
        // Don't retry on connection errors more than once
        if (error.message.includes("Failed to fetch") && failureCount >= 1) {
          return false;
        }
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query mutation hook for triggering cluster analysis.
 * @returns Mutation object for triggering AI analysis on a cluster
 */
export function useTriggerClusterAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (symbol: string) => triggerClusterAnalysis(symbol),
    onSuccess: (_data, symbol) => {
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.clusterSummary });
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.clusterDetail(symbol) });
    },
  });
}

/**
 * React Query mutation hook for discarding a cluster.
 * @returns Mutation object for discarding a cluster
 */
export function useDiscardCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (symbol: string) => discardCluster(symbol),
    onSuccess: (_data, symbol) => {
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.clusterSummary });
      queryClient.removeQueries({ queryKey: MARKET_DATA_KEYS.clusterDetail(symbol) });
    },
  });
}

/**
 * React Query mutation hook for sending cluster notification.
 * @returns Mutation object for sending Telegram notification for a cluster
 */
export function useClusterNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (symbol: string) => sendClusterNotification(symbol),
    onSuccess: (_data, symbol) => {
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.clusterDetail(symbol) });
    },
  });
}

/**
 * React Query hook for fetching news feed data.
 * @returns Query result with news feed items
 */
export function useNewsFeed() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.newsFeed,
    queryFn: fetchNewsFeed,
    staleTime: 2 * 60_000, // 2 minutes - articles don't change every second
    refetchInterval: 3 * 60_000, // Refresh every 3 minutes (reduced from 1 minute)
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query hook for fetching collapse monitor data.
 * @returns Query result with collapse monitor dashboard data
 */
export function useCollapseMonitor() {
  return useQuery({
    queryKey: MARKET_DATA_KEYS.collapseMonitor,
    queryFn: fetchCollapseMonitor,
    refetchInterval: 60_000,
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

/**
 * React Query mutation hook for executing collapse monitor actions.
 * @returns Mutation object for executing emergency actions
 */
export function useExecuteCollapseAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { actionId: string; reason?: string }) =>
      executeCollapseAction(variables.actionId, { reason: variables.reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MARKET_DATA_KEYS.collapseMonitor });
    },
  });
}

/**
 * React Query hook for fetching crypto bubbles data.
 * @param symbols - Optional array of symbols to filter
 * @returns Query result with crypto bubble data
 */
export function useCryptoBubbles(symbols?: string[]) {
  return useQuery({
    queryKey: [...MARKET_DATA_KEYS.cryptoBubbles, symbols?.join(",") ?? "all"],
    queryFn: () => fetchCryptoBubbles(symbols),
    staleTime: 5 * 60_000, // 5 minutes - crypto prices change frequently but not every second
    refetchInterval: 5 * 60_000, // Refresh every 5 minutes (reduced from 2 minutes)
    retry: (failureCount, error) => {
      // Don't retry on connection errors more than once
      if (error instanceof Error && error.message.includes("Failed to fetch") && failureCount >= 1) {
        return false;
      }
      // Retry up to 2 times for server errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
  });
}

